<template>
  <HeadTitle
    title="Usuarios"
    description="Lista de usuarios registrados en el sistema. Puedes filtrar por compañia seleccionada"
  />
  <br />
  <Toolset ref="toolset" :searcher="onSearch" :is-adding="false">
    <BulkSupervisorActions />
  </Toolset>
  <br />
  <DataTable
    class="tabla mb-5"
    :value="users"
    lazy
    paginator
    :rows="lazyParams.limit"
    :rowsPerPageOptions="[5, 10, 20, 50]"
    tableStyle="min-width: 50rem"
    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
    :totalRecords="totalRecords"
    :currentPageReportTemplate="currentPageReportTemplate"
    scrollable
    @page="onPage"
    @sort="onSort"
    scrollHeight="600px"
  >
    <Column field="name" sortable header="Nombre"></Column>
    <Column field="email" sortable header="Correo"></Column>
    <Column field="role.name" header="Rol">
      <template #body="{ data }">
        {{ roleNames.get(data.role.name) }}
      </template>
    </Column>
    <Column field="enabled" header="Estado">
      <template #body="{ data, field }">
        <Chip class="bg-pink-200 p-2" v-if="!data[field]">
          <label class="text-sm text-pink-700 font-normal">inactivo</label>
        </Chip>
        <Chip class="bg-green-100 p-2" v-else>
          <label class="text-sm text-green-700 font-normal">activo</label>
        </Chip>
      </template>
    </Column>
    <Column field="userStatus" header="Autenticación">
      <template #body="{ data }">
        {{ TranslateUserStatus(data.userStatus) }}
      </template>
    </Column>
    <Column field="updatedAt" header="Última actualización" sortable>
      <template #body="{ data, field }">
        <span>
          {{ formatDateWithHour(data[field]) }}
        </span>
      </template>
    </Column>
    <Column header="Acciones">
      <template #body="{ data }">
        <ConfirmPopup></ConfirmPopup>
        <div class="actions">
          <Button
            class="action-btn view p-button-sm shadow-1"
            icon="pi pi-eye"
            @click="goToDetails(data['username'])"
          />
          <Button
            class="action-btn edit p-button-sm bg-gray-300 border-gray-400 mr-2 shadow-1 text-primary"
            icon="pi pi-lock"
            @click="
              enableOrDisableUser($event, data['username'], data['enabled'])
            "
            severity="secondary"
          />
          <Button
            class="action-btn delete p-button-sm"
            icon="pi pi-trash"
            @click="deleteClient($event, data['username'])"
            severity="danger"
          />
        </div>
      </template>
    </Column>
  </DataTable>
  <Footer>
    <template #left>
      <AuditButton
        :root-id="company?.id"
        :only-deleted="true"
        label="Ver registros eliminados"
        :entities="['User']"
        :disabled="!company?.id"
      />
    </template>
  </Footer>
</template>
<script setup lang="ts">
  import { computed, onMounted, reactive, ref, watch } from 'vue'
  import Toolset from '@/shared/components/Toolset.vue'
  import Footer from '@/shared/components/Footer.vue'
  import AuditButton from '@/shared/components/AuditButton.vue'
  import User from '../models/User'
  import { deleteUser, enableUser, getUsers } from '../services/UserService'
  import { useConfirm } from 'primevue/useconfirm'
  import useNotifications from '@/store/notification'
  import { useRouter } from 'vue-router'
  import useCompany from '@/store/company'
  import { storeToRefs } from 'pinia'
  import { formatDateWithHour } from '@/shared/utils/fomartDateWithHours'
  import useLoading from '@/store/load'
  import TranslateUserStatus from '@/shared/utils/translateUserStatus'
  import roleNames from '@/shared/utils/rolesName'
  import HeadTitle from '@/shared/components/HeadTitle.vue'
  import BulkSupervisorActions from '../components/BulkSupervisorActions.vue'
  import type {
    DataTablePageEvent,
    DataTableSortEvent,
  } from 'primevue/datatable'

  const toolset = ref()
  const router = useRouter()
  const confirm = useConfirm()
  const notificationStore = useNotifications()
  const companyStore = useCompany()
  const loadingStore = useLoading()
  const { company } = storeToRefs(companyStore)

  const users = ref<User[]>([])
  const totalRecords = ref(0)

  const lazyParams = reactive({
    offset: 1,
    limit: 10,
    sortField: 'updatedAt',
    sortOrder: -1,
    search: '',
  })

  const currentPageReportTemplate = computed(() => {
    return `{first} a {last} de ${totalRecords.value}`
  })

  watch(
    [company, lazyParams],
    () => {
      refreshData()
    },
    { deep: true }
  )

  onMounted(async () => {
    await refreshData()
  })

  const refreshData = async () => {
    loadingStore.setLoadingState()
    try {
      const { data, meta } = await getUsers({
        companyId: company.value?.id,
        limit: lazyParams.limit,
        offset: lazyParams.offset,
        search: lazyParams.search,
        sortField: lazyParams.sortField,
        sortOrder: lazyParams.sortOrder === 1 ? 'asc' : 'desc',
      })

      users.value = data
      totalRecords.value = meta.total
    } catch (error) {
      console.error('Failed to fetch users:', error)
      notificationStore.addErrorNotification({
        detail: 'Error al cargar los usuarios',
        summary: 'Error',
        severity: 'error',
      })
    } finally {
      loadingStore.dismissLoadingState()
    }
  }

  const onSearch = () => {
    lazyParams.search = toolset.value.search
    lazyParams.offset = 1
    refreshData()
  }

  const onPage = (event: DataTablePageEvent) => {
    console.log(event.page, event.first, event.rows)
    lazyParams.offset = event.page + 1
    lazyParams.limit = event.rows
  }

  const onSort = (event: DataTableSortEvent) => {
    lazyParams.sortField = event.sortField as string
    lazyParams.sortOrder = event.sortOrder as number
  }

  const goToDetails = (username: string) => {
    router.push('/users/' + username.replace(/\./g, '-'))
  }

  const enableOrDisableUser = (
    event: any,
    username: string,
    enable: boolean
  ) => {
    confirm.require({
      target: event.currentTarget,
      message: `¿Realmente quieres ${
        enable ? 'inhabilitar' : 'habilitar'
      } este usuario?`,
      icon: 'pi pi-info-circle',
      acceptLabel: 'aceptar',
      rejectLabel: 'cancelar',
      accept: async () => {
        const response = await enableUser(enable, username)
        if (response === 'OK') {
          notificationStore.addSuccesNotification({
            detail: `200`,
            severity: 'success',
            summary: `El estado ha cambiado con exito.`,
          })

          refreshData()
        }
      },
      reject: () => {},
    })
  }

  const deleteClient = (event: any, username: string) => {
    confirm.require({
      target: event.currentTarget,
      message: '¿Realmente quieres eliminar este usuario?',
      icon: 'pi pi-info-circle',
      acceptClass: 'p-button-danger',
      acceptLabel: 'aceptar',
      rejectLabel: 'cancelar',
      accept: async () => {
        const response = await deleteUser(username)
        if (response === 'OK') {
          notificationStore.addSuccesNotification({
            detail: `200`,
            severity: 'success',
            summary: `Usuario eliminado con exito.`,
          })
          refreshData()
        }
      },
      reject: () => {},
    })
  }
</script>
<style scoped>
  .tabla {
    margin-top: 1rem;
    box-shadow: 0px 1px 7px rgba(0, 0, 0, 0.1),
      0px 4px 5px -2px rgba(0, 0, 0, 0.12),
      0px 10px 15px -5px rgba(0, 0, 0, 0.2);
    border-radius: 16px;
    width: 90%;
  }

  .actions {
    display: flex;
    max-width: 180px;
    justify-content: space-between;
    flex-direction: row;
  }

  .action-btn {
    height: 20px;
    width: 20px;
    margin-right: 10px;
  }

  :deep(.p-column-title) {
    font-size: 14px;
  }

  :deep(.p-datatable-tbody) {
    font-size: 14px;
  }

  :deep(.p-paginator) {
    display: flex;
    align-items: center;
    justify-content: end;
    flex-wrap: wrap;
    font-size: 14px;
  }

  :deep(.p-button-label) {
    font-size: 1rem;
    font-weight: 400;
  }
</style>
